import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { RiskScore } from '../components/RiskScore';
import { Warnings } from '../components/Warnings';
import { TradePanel } from '../components/TradePanel';
import { HolderChart } from '../components/HolderChart';
import { PriceChart } from '../components/PriceChart';
import { OrderPanel } from '../components/OrderPanel';
import { AlertsPanel } from '../components/AlertsPanel';
import { CreatorProfile } from '../components/CreatorProfile';
import { AIAnalysisPanel } from '../components/AIAnalysisPanel';
import { ReportBadge } from '../components/ReportBadge';
import { ReportSummary } from '../components/ReportSummary';
import { SentimentCard } from '../components/SentimentCard';
import { GraduationTimer } from '../components/GraduationTimer';
import { TradeSummaryFeed } from '../components/TradeSummaryFeed';
import { CreatorNetworkGraph } from '../components/CreatorNetworkGraph';
import { RiskCardDownloader } from '../components/RiskCardDownloader';
import { analyzeToken, getTokenPrice, type TokenAnalysis, type TokenPrice } from '../lib/api';
import { generateMockTransactions } from '../lib/trade-summaries';
import { generateMockRecentTrades } from '../lib/graduation';
import { Order } from '../lib/orders';
import { startAlertPolling, stopAlertPolling } from '../lib/alerts';
import { shortenAddress } from '../lib/utils';
import { analytics } from '../lib/analytics';

// Price update interval (5 seconds)
const PRICE_UPDATE_INTERVAL = 5000;

// Social Icons
const TwitterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TelegramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

const WebsiteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const DiscordIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export function Token() {
  const { address } = useParams<{ address: string }>();
  const navigate = useNavigate();
  const { connected, publicKey } = useWallet();

  const [analysis, setAnalysis] = useState<TokenAnalysis | null>(null);
  const [livePrice, setLivePrice] = useState<TokenPrice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedSafeLink, setCopiedSafeLink] = useState(false);
  const priceIntervalRef = useRef<number | null>(null);

  const walletAddress = publicKey?.toString() ?? null;

  const copyAddress = useCallback(async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [address]);

  const copySafeLink = useCallback(async () => {
    if (!address) return;
    try {
      // Use the API share endpoint which provides dynamic OG tags
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.skop.site';
      const smartLink = `${apiUrl}/share/${address}`;
      await navigator.clipboard.writeText(smartLink);
      setCopiedSafeLink(true);
      setTimeout(() => setCopiedSafeLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy safe link:', err);
    }
  }, [address]);

  // Initial token analysis
  useEffect(() => {
    if (!address) return;

    setLoading(true);
    setError(null);

    // Track page view
    analytics.tokenViewed(address, 'direct');

    analyzeToken(address)
      .then((result) => {
        setAnalysis(result);
        analytics.tokenAnalyzed(address, result.riskLevel);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [address]);

  // Real-time price polling
  useEffect(() => {
    if (!address || loading) return;

    const fetchPrice = async () => {
      try {
        const price = await getTokenPrice(address);
        setLivePrice(price);
      } catch (err) {
        console.debug('Price fetch failed:', err);
      }
    };

    // Fetch immediately
    fetchPrice();

    // Set up polling
    priceIntervalRef.current = window.setInterval(fetchPrice, PRICE_UPDATE_INTERVAL);

    return () => {
      if (priceIntervalRef.current) {
        clearInterval(priceIntervalRef.current);
      }
    };
  }, [address, loading]);

  // Start alert polling when wallet is connected
  useEffect(() => {
    if (walletAddress) {
      startAlertPolling(walletAddress);
    }
    return () => {
      stopAlertPolling();
    };
  }, [walletAddress]);

  // Use live price if available, otherwise use analysis price
  const currentPrice = livePrice?.priceUsd ?? analysis?.priceUsd ?? 0;
  const priceChange = livePrice?.priceChange24h ?? analysis?.priceChange24h ?? 0;
  const marketCap = livePrice?.marketCap ?? analysis?.marketCap ?? 0;

  if (loading) {
    return (
      <div style={{ paddingTop: '48px', textAlign: 'center' }}>
        <div
          className="animate-spin"
          style={{
            width: '40px',
            height: '40px',
            margin: '0 auto 16px',
            border: '3px solid var(--border)',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
          }}
        />
        <p style={{ color: 'var(--text-secondary)' }}>Analyzing token...</p>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div style={{ paddingTop: '48px', textAlign: 'center' }}>
        <div style={{
          padding: '24px',
          background: 'rgba(255, 59, 59, 0.1)',
          border: '1px solid rgba(255, 59, 59, 0.3)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: '16px',
        }}>
          <p style={{ color: 'var(--error)', marginBottom: '8px' }}>
            {error || 'Failed to analyze token'}
          </p>
          <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
            {address && shortenAddress(address, 8)}
          </p>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '16px', paddingBottom: '120px' }}>
      {/* Token Header */}
      <div className="card" style={{ textAlign: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '12px' }}>
          {analysis.image && (
            <img
              src={analysis.image}
              alt={analysis.name}
              style={{ width: '48px', height: '48px', borderRadius: '50%' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: 700 }}>{analysis.name}</h1>
            <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>{analysis.symbol}</p>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <span style={{
            padding: '4px 8px',
            fontSize: '11px',
            fontWeight: 600,
            background: 'var(--accent-muted)',
            color: 'var(--accent)',
            borderRadius: 'var(--radius-sm)',
          }}>
            {analysis.platform}
          </span>
          {analysis.graduated && (
            <span style={{
              padding: '4px 8px',
              fontSize: '11px',
              fontWeight: 600,
              background: 'rgba(0, 255, 136, 0.1)',
              color: 'var(--success)',
              borderRadius: 'var(--radius-sm)',
            }}>
              Graduated
            </span>
          )}
          <ReportBadge tokenAddress={analysis.address} />
        </div>

        {/* Address with Copy Button */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
          <button
            onClick={copyAddress}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              color: copied ? 'var(--success)' : 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
              background: 'var(--surface-secondary)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {shortenAddress(analysis.address, 8)}
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
          <button
            onClick={copySafeLink}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 10px',
              fontSize: '11px',
              fontWeight: 500,
              color: copiedSafeLink ? 'var(--success)' : 'var(--accent)',
              background: copiedSafeLink ? 'rgba(0, 255, 136, 0.1)' : 'var(--accent-muted)',
              border: '1px solid',
              borderColor: copiedSafeLink ? 'var(--success)' : 'var(--accent)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            title="Copy protected share link"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            {copiedSafeLink ? 'Copied!' : 'Safe Link'}
          </button>
        </div>

        {/* Social Links */}
        {analysis.socials && Object.keys(analysis.socials).length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
            {analysis.socials.website && (
              <a
                href={analysis.socials.website}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  color: 'var(--text-secondary)',
                  background: 'var(--surface-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  transition: 'all 0.15s ease',
                }}
                title="Website"
              >
                <WebsiteIcon />
              </a>
            )}
            {analysis.socials.twitter && (
              <a
                href={analysis.socials.twitter}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  color: 'var(--text-secondary)',
                  background: 'var(--surface-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  transition: 'all 0.15s ease',
                }}
                title="Twitter"
              >
                <TwitterIcon />
              </a>
            )}
            {analysis.socials.telegram && (
              <a
                href={analysis.socials.telegram}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  color: 'var(--text-secondary)',
                  background: 'var(--surface-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  transition: 'all 0.15s ease',
                }}
                title="Telegram"
              >
                <TelegramIcon />
              </a>
            )}
            {analysis.socials.discord && (
              <a
                href={analysis.socials.discord}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  color: 'var(--text-secondary)',
                  background: 'var(--surface-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  transition: 'all 0.15s ease',
                }}
                title="Discord"
              >
                <DiscordIcon />
              </a>
            )}
          </div>
        )}
      </div>

      {/* Risk Score */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <RiskScore score={analysis.riskScore} level={analysis.riskLevel} />
      </div>

      {/* AI Analysis */}
      <div style={{ marginBottom: '16px' }}>
        <AIAnalysisPanel token={analysis} />
      </div>

      {/* Risk Card Downloader */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Share Analysis</h3>
        <RiskCardDownloader
          tokenName={analysis.name}
          tokenSymbol={analysis.symbol}
          tokenAddress={analysis.address}
          riskScore={analysis.riskScore}
          riskLevel={analysis.riskLevel}
          creatorTokenCount={analysis.creatorTokenCount}
          top5HolderPercent={analysis.top5HolderPercent}
          bundledBuys={analysis.bundledBuys}
        />
      </div>

      {/* Social Sentiment */}
      <div style={{ marginBottom: '16px' }}>
        <SentimentCard tokenAddress={analysis.address} />
      </div>

      {/* Community Reports */}
      <div style={{ marginBottom: '16px' }}>
        <ReportSummary
          tokenAddress={analysis.address}
          tokenSymbol={analysis.symbol}
          tokenName={analysis.name}
        />
      </div>

      {/* Price Chart */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <PriceChart tokenAddress={analysis.address} height={140} />
      </div>

      {/* Warnings */}
      {analysis.warnings.length > 0 && (
        <Warnings warnings={analysis.warnings} />
      )}

      {/* Creator Profile */}
      {(() => {
        const creatorHolder = analysis.holders.find(h => h.isCreator);
        return creatorHolder ? (
          <>
            <div style={{ marginBottom: '16px' }}>
              <CreatorProfile creatorAddress={creatorHolder.address} />
            </div>
            {/* Creator Network Graph */}
            <div className="card" style={{ marginBottom: '16px', padding: '16px' }}>
              <CreatorNetworkGraph
                creatorAddress={creatorHolder.address}
                width={Math.min(window.innerWidth - 64, 560)}
                height={300}
              />
            </div>
          </>
        ) : null;
      })()}

      {/* Bonding Curve Progress */}
      {analysis.bondingCurve && !analysis.graduated && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>Bonding Curve</span>
            <span style={{ fontSize: '13px', color: 'var(--accent)', fontWeight: 600 }}>
              {analysis.bondingCurve.progress.toFixed(1)}%
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '8px',
            background: 'var(--surface-secondary)',
            borderRadius: '4px',
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${analysis.bondingCurve.progress}%`,
              height: '100%',
              background: analysis.bondingCurve.progress > 90
                ? 'linear-gradient(90deg, var(--accent), var(--success))'
                : 'var(--accent)',
              borderRadius: '4px',
              transition: 'width 0.3s ease',
            }} />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '8px',
            fontSize: '11px',
            color: 'var(--text-tertiary)',
          }}>
            <span>{analysis.bondingCurve.currentSol.toFixed(1)} SOL</span>
            <span>{analysis.bondingCurve.targetSol} SOL to graduate</span>
          </div>
        </div>
      )}

      {/* Graduation Timer - shows predicted graduation time */}
      {analysis.bondingCurve && !analysis.graduated && (
        <div style={{ marginBottom: '16px' }}>
          <GraduationTimer
            bondingCurveProgress={analysis.bondingCurve.progress}
            recentTrades={generateMockRecentTrades()}
            isGraduated={analysis.graduated}
          />
        </div>
      )}

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
        <StatCard
          label="Price"
          value={`$${currentPrice.toFixed(6)}`}
          subValue={priceChange !== 0 ? `${priceChange >= 0 ? '+' : ''}${priceChange.toFixed(1)}%` : undefined}
          subValueColor={priceChange >= 0 ? 'var(--success)' : 'var(--error)'}
          live={!!livePrice}
        />
        <StatCard label="Market Cap" value={formatMarketCap(marketCap)} />
        <StatCard label="Holders" value={formatNumber(analysis.holderCount)} />
        <StatCard label="24h Volume" value={formatMarketCap(analysis.volume24h)} />
        <StatCard label="Creator Tokens" value={analysis.creatorTokenCount.toString()} warning={analysis.creatorTokenCount > 5} />
        <StatCard label="Top 5 Hold" value={`${analysis.top5HolderPercent.toFixed(1)}%`} warning={analysis.top5HolderPercent > 50} />
      </div>

      {/* Holders */}
      {analysis.holders && analysis.holders.length > 0 && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Top Holders</h3>
          <HolderChart holders={analysis.holders} />
        </div>
      )}

      {/* Recent Activity Feed */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Recent Activity</h3>
        <TradeSummaryFeed
          transactions={generateMockTransactions(analysis.address)}
          maxItems={8}
          showRecap={true}
        />
      </div>

      {/* Price Alerts */}
      <div style={{ marginBottom: '16px' }}>
        <AlertsPanel
          tokenAddress={analysis.address}
          tokenSymbol={analysis.symbol}
          tokenName={analysis.name}
          currentPrice={currentPrice}
        />
      </div>

      {/* Order Panel - Limit orders, stop loss, take profit */}
      <OrderPanel
        tokenAddress={analysis.address}
        tokenSymbol={analysis.symbol}
        currentPriceUsd={livePrice?.priceUsd || analysis.priceUsd}
        onOrderTriggered={(order: Order) => {
          console.log('Order triggered:', order);
        }}
      />

      {/* Trade Panel - Fixed at bottom */}
      <TradePanel
        token={analysis}
        connected={connected}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  warning,
  subValue,
  subValueColor,
  live,
}: {
  label: string;
  value: string;
  warning?: boolean;
  subValue?: string;
  subValueColor?: string;
  live?: boolean;
}) {
  return (
    <div className="card" style={{ padding: '12px', position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
        <p style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{label}</p>
        {live && (
          <span style={{
            width: '6px',
            height: '6px',
            background: 'var(--success)',
            borderRadius: '50%',
            animation: 'pulse 2s infinite',
          }} />
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
        <p style={{
          fontSize: '16px',
          fontWeight: 600,
          color: warning ? 'var(--risk-high)' : 'var(--text-primary)',
        }}>
          {value}
        </p>
        {subValue && (
          <span style={{
            fontSize: '12px',
            fontWeight: 500,
            color: subValueColor || 'var(--text-secondary)',
          }}>
            {subValue}
          </span>
        )}
      </div>
    </div>
  );
}

function formatMarketCap(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toString();
}
